import React, { useState, useRef } from 'react';
import './CvUploadForm.css';

function CvUploadForm({ onUpload, uploading }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!file) {
      return 'No file selected';
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only PDF, DOC, and DOCX files are allowed';
    }

    if (file.size > MAX_SIZE) {
      return 'File size must be less than 10MB';
    }

    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        alert(error);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const error = validateFile(file);
      if (error) {
        alert(error);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    await onUpload(selectedFile);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="cv-upload-form">
      <h2>Upload Your CV</h2>

      <form onSubmit={handleSubmit}>
        <div
          className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={uploading}
          />

          <div className="drop-zone-content">
            <div className="upload-icon">📄</div>
            {selectedFile ? (
              <div className="selected-file-info">
                <p className="file-name">{selectedFile.name}</p>
                <p className="file-size">{formatFileSize(selectedFile.size)}</p>
              </div>
            ) : (
              <>
                <p className="drop-text">
                  <strong>Click to browse</strong> or drag and drop
                </p>
                <p className="allowed-types">PDF, DOC, or DOCX (max 10MB)</p>
              </>
            )}
          </div>
        </div>

        <div className="form-actions">
          {selectedFile && !uploading && (
            <button type="button" onClick={handleClear} className="btn btn-secondary">
              Clear
            </button>
          )}
          <button
            type="submit"
            disabled={!selectedFile || uploading}
            className="btn btn-primary"
          >
            {uploading ? (
              <>
                <span className="spinner-small"></span>
                Uploading...
              </>
            ) : (
              'Upload CV'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CvUploadForm;
